import { computed } from '@preact/signals-react';
import { t } from 'i18next';
import { useState } from 'react';
import { OrganizationExportOptions } from '../../options/OrganizationExportOptions';
import { CsvExportManager } from '../../services/CsvExportManager';
import { PrintExportManager } from '../../services/PrintExportManager';
import { DashboardExportState, ExportFormat } from '../states/DashboardExportState';
import { ExportDrawerChips, ExportsDrawer } from './components/ExportsDrawer';
import { CsvSelector } from './components/selectors/CsvSelector';
import { PrintSelector } from './components/selectors/PrintSelector';
import { formatRange } from '../../../organization/dashboard/utils';
import { getLabel as getOverviewTypeLabel, OverviewType } from '../../../common/dashboards/enums/OverviewType';
import { TrendDataScale } from '../../../common/dashboards/models/TrendDataScale';

type Props = {
  onError: (e: unknown) => void;
};

export const DashboardExportDrawer = ({ onError }: Props) => {
  const state = DashboardExportState.getInstance();
  const [printManager] = useState(new PrintExportManager());
  const [csvManager] = useState(new CsvExportManager());

  const [pending, setPending] = useState(false);

  const chips = computed<ExportDrawerChips[]>(() => {
    const stateValues = state.peek();
    const projects = stateValues.projects.map((p) => p.getLabel());
    const profiles = stateValues.profiles.map((p) => p.toFilterFormat(true).label);
    const tags = [...stateValues.tags];

    if (projects.length === 0) {
      projects.push(t('allProjects'));
    }
    if (profiles.length === 0) {
      profiles.push(t('allProfiles'));
    }
    if (tags.length === 0) {
      tags.push(t('allTags'));
    }

    const exceptionLabel = stateValues.withExceptions ? t('withExceptions') : t('withoutExceptions');
    let overviewTypes = stateValues.selectedOverviews.map((overviewType) => getOverviewTypeLabel(overviewType as OverviewType));
    if (overviewTypes.length === 0) {
      overviewTypes.push(t('noOverviewTypesSelected'));
    } else if (overviewTypes.length === Object.values(OverviewType).length) {
      overviewTypes = [t('allOverviewTypes')];
    }

    return [
      { title: t('projects'), chips: projects },
      { title: t('dateRange'), chips: [formatRange(stateValues.dateRange, TrendDataScale.DAY, false)] },
      { title: t('tags'), chips: tags },
      { title: t('profiles'), chips: profiles },
      { title: t('exceptions'), chips: [exceptionLabel] },
      { title: t('overviewTypes'), chips: overviewTypes },
    ];
  });

  const onExportClick = async () => {
    const { name, projects, profiles, tags, selectedOverviews, trends, withExceptions, includePassed, dateRange, format } = state.peek();

    const exportOptions: OrganizationExportOptions = {
      projects,
      profiles,
      tags,
      overviews: selectedOverviews,
      trends,
      withExceptions,
      includePassed,
      dateRange,
    };

    try {
      setPending(true);

      const manager = format === ExportFormat.CSV ? csvManager : printManager;
      await manager.exportOrganizationDashboard(name, exportOptions);

      setPending(false);
    } catch (e: unknown) {
      setPending(false);
      onError(e);
    }
  };

  return (
    <ExportsDrawer
      title={t('exportTitles.org')}
      disabled={!state.ready.value || pending}
      chips={chips.value}
      selectors={[
        <PrintSelector
          key="print"
          description={t('exportFormats.print.org')}
          selected={state.format.value === ExportFormat.PRINT}
          loading={pending}
          onSelected={() => state.setFormat(ExportFormat.PRINT)}
        />,
        <CsvSelector
          key="csv"
          description={t('exportFormats.csv.org')}
          selected={state.format.value === ExportFormat.CSV}
          loading={pending}
          onSelected={() => state.setFormat(ExportFormat.CSV)}
        />,
      ]}
      actionButtonProps={{
        children: state.format.value === ExportFormat.PRINT ? t('print') : t('download'),
        onClick: onExportClick,
        disabled: pending || !state.ready.value,
        loading: pending,
      }}
    />
  );
};
